
import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useBooking } from '@/contexts/BookingContext';
import Header from '@/components/sections/Header';
import LocationSection from '@/components/sections/LocationSection';
import Footer from '@/components/sections/Footer';
import { blogCategories as categories } from '@/data/blogPosts';
import { useBlogPosts, BlogPost } from '@/hooks/useBlogPosts';
import Breadcrumbs from '@/components/Breadcrumbs';

const PostCard: React.FC<{ post: BlogPost }> = ({ post }) => (
  <article className="group cursor-pointer flex flex-col gap-5">
    <Link to={`/blog/${post.slug}`} className="contents">
      <figure className="w-full aspect-video rounded-2xl overflow-hidden bg-dark-surface border border-white/8 relative m-0">
        {post.image ? (
          <img src={post.image} alt={post.title} title={post.title} className="img-zoom" loading="lazy" decoding="async" width="640" height="360" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <div className="w-10 h-10 rounded-full border border-gold/20 flex items-center justify-center" aria-hidden="true">
              <div className="w-2 h-2 rounded-full bg-gold/40" />
            </div>
            <span className="text-light-muted/40 text-[10px] uppercase tracking-widest">Foto em breve</span>
          </div>
        )}
        <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest bg-dark-base/80 text-gold border border-gold/20 rounded-full px-3 py-1 backdrop-blur-sm">
          {post.category}
        </span>
        <div className="absolute inset-0 bg-gradient-to-t from-dark-base/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" aria-hidden="true" />
      </figure>
      <header className="flex flex-col gap-2">
        <h3 className="text-lg text-light-primary font-medium leading-snug line-clamp-2 group-hover:text-gold transition-colors duration-300">
          {post.title}
        </h3>
        <p className="text-sm text-light-muted leading-relaxed line-clamp-2">{post.excerpt}</p>
        {post.date && (
          <time className="text-xs text-light-muted/50 font-medium mt-1">{post.date}</time>
        )}
      </header>
    </Link>
  </article>
);

const BlogPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [activeCategory, setActiveCategory] = useState<string | null>(() => searchParams.get('cat') ?? null);
  const { posts: allPosts, loading } = useBlogPosts();

  useEffect(() => {
    const q = searchParams.get('q');
    const cat = searchParams.get('cat');
    if (q !== null) setSearch(q);
    if (cat !== null) setActiveCategory(cat);
  }, [searchParams]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const { handleWhatsAppOpen } = useBooking();

  const filtered = useMemo(() => {
    return allPosts.filter(p => {
      const matchCat = !activeCategory || p.category === activeCategory;
      const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [search, activeCategory, allPosts]);

  const categoriesShown = showAllCategories ? categories : categories.slice(0, 5);
  const recentPosts = allPosts.slice(0, 3);

  const categoryCount = (cat: string) => allPosts.filter(p => p.category === cat).length;

  return (
    <>
      <Helmet>
        <title>Blog | Dr. Luis Brunello — Cirurgia Plástica em Curitiba</title>
        <meta name="description" content="Artigos sobre cirurgia plástica, pós-operatório, cuidados e procedimentos. Conteúdo educativo do Dr. Luis Brunello, cirurgião plástico em Curitiba." />
        <link rel="canonical" href="https://luisbrunello.com.br/blog" />
        <meta property="og:title" content="Blog | Dr. Luis Brunello — Cirurgia Plástica em Curitiba" />
        <meta property="og:description" content="Artigos sobre cirurgia plástica, pós-operatório, cuidados e procedimentos. Conteúdo educativo do Dr. Luis Brunello, cirurgião plástico em Curitiba." />
        <meta property="og:url" content="https://luisbrunello.com.br/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Dr. Luis Brunello" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:image" content="https://luisbrunello.com.br/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog | Dr. Luis Brunello — Cirurgia Plástica em Curitiba" />
        <meta name="twitter:description" content="Artigos sobre cirurgia plástica, pós-operatório, cuidados e procedimentos." />
        <meta name="twitter:image" content="https://luisbrunello.com.br/og-image.jpg" />
      </Helmet>

      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={() => {}}
        onOpenBooking={() => handleWhatsAppOpen()}
      />

      {/* Banner */}
      <header className="relative flex items-center justify-center text-center overflow-hidden" style={{ minHeight: '260px', paddingTop: '96px' }}>
        <div className="absolute inset-0 bg-dark-elevated" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-dark-base/60 via-transparent to-dark-base/40" aria-hidden="true" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] decorative-blur decorative-blur-primary opacity-10 pointer-events-none" aria-hidden="true" />
        <div className="relative z-10 px-6 py-12 md:py-16">
          <p className="subtitle-wide text-gold mb-3 block">Conteúdo</p>
          <h1 className="font-display text-4xl md:text-5xl text-light-primary font-normal uppercase tracking-[0.08em]">
            Blog
          </h1>
          <div className="mt-4 w-12 h-px mx-auto" style={{ background: 'hsl(var(--color-gold))' }} aria-hidden="true" />
          <p className="mt-4 text-light-muted text-sm md:text-base max-w-md mx-auto leading-relaxed">
            Artigos sobre cirurgia plástica, cuidados pré e pós-operatórios e informações para você decidir com clareza.
          </p>
        </div>
      </header>

      <main className="bg-dark-base py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-12">

          <Breadcrumbs
            items={[
              { label: 'Home', href: '/' },
              { label: 'Blog' },
            ]}
            className="mb-8 max-w-4xl mx-auto"
          />

          {/* Search + Category filter bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10 max-w-4xl mx-auto">
            <div className="relative">
              <select
                name="category"
                aria-label="Filtrar por categoria"
                value={activeCategory ?? ''}
                onChange={e => { setActiveCategory(e.target.value || null); }}
                className="appearance-none h-11 pl-4 pr-10 rounded-xl bg-dark-surface border border-white/10 text-light-muted text-sm focus:outline-none focus:border-gold/40 cursor-pointer"
              >
                <option value="">Todas as categorias</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-light-muted/50" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </div>

            <div className="relative flex-1">
              <input
                name="q"
                aria-label="Buscar matérias"
                type="text"
                placeholder="Busque por uma matéria..."
                value={search}
                onChange={e => { setSearch(e.target.value); }}
                className="w-full h-11 pl-5 pr-12 rounded-xl bg-dark-surface border border-white/10 text-light-primary text-sm placeholder:text-light-muted/40 focus:outline-none focus:border-gold/40 transition-colors"
              />
              <button aria-label="Buscar" className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg bg-dark-base flex items-center justify-center text-light-muted hover:text-gold transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 items-start">

            {/* Posts */}
            <div>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filtered.length > 0 ? (
                <>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6 list-none p-0 m-0">
                    {filtered.map(post => (
                      <li key={post.slug} className="list-none">
                        <PostCard post={post} />
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="py-16 text-center text-light-muted/50 text-sm">
                  Nenhum artigo encontrado para esta busca.
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-8">
              <div className="bg-dark-surface border border-white/5 rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-light-primary uppercase tracking-widest mb-5">Categorias</h2>
                <ul className="space-y-2">
                  {categoriesShown.map(cat => (
                    <li key={cat}>
                      <button
                        onClick={() => { setActiveCategory(activeCategory === cat ? null : cat); }}
                        className={`w-full flex items-center justify-between text-sm py-2 px-3 rounded-lg transition-all duration-200 ${activeCategory === cat ? 'bg-gold/10 text-gold border border-gold/20' : 'text-light-muted hover:text-light-primary hover:bg-white/5'}`}
                      >
                        <span>{cat}</span>
                        <span className={`text-xs rounded-full px-2 py-0.5 ${activeCategory === cat ? 'bg-gold/20 text-gold' : 'bg-white/5 text-light-muted/50'}`}>
                          {categoryCount(cat)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {categories.length > 5 && (
                  <button
                    onClick={() => setShowAllCategories(v => !v)}
                    className="mt-4 w-full text-xs text-gold/70 hover:text-gold transition-colors text-center"
                  >
                    {showAllCategories ? 'Ver menos' : `Ver mais ${categories.length - 5} categorias`}
                  </button>
                )}
              </div>

              <div className="bg-dark-surface border border-white/5 rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-light-primary uppercase tracking-widest mb-5">Últimos artigos</h2>
                <ul className="space-y-5">
                  {recentPosts.map(post => (
                    <li key={post.slug}>
                      <Link to={`/blog/${post.slug}`} className="group flex gap-3 items-start">
                        <div className="w-14 h-14 rounded-lg bg-dark-base border border-white/5 shrink-0 overflow-hidden flex items-center justify-center">
                          {post.image ? (
                            <img src={post.image} alt={post.title} title={post.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width="56" height="56" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-gold/30" />
                          )}
                        </div>
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-xs text-light-muted/50">{post.date}</span>
                          <span className="text-sm text-light-muted group-hover:text-gold transition-colors line-clamp-2 leading-snug">
                            {post.title}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <LocationSection onOpenBooking={() => handleWhatsAppOpen()} />
      <Footer />
    </>
  );
};

export default BlogPage;
